import { DirectHotelMatch } from './DirectHotelMatch';
import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Checkbox, FormControlLabel, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { client } from '@frontend/features/common/services';
import { DestinationPicker } from './DestinationPicker';
import type { Candidate } from './AddSelection';
import { catalogAdminKey } from './queries';

type Account = { connector_account_id: string; display_name: string; status: string; access_codes: string[] };
type Outcome = { hotel_id: string; display_name: string; status: string; code?: string; job_id?: string };
export function DirectCatalogLinking({ onBusyChange }: { onBusyChange(busy: boolean): void }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState('country');
  const [country, setCountry] = useState('');
  const [destination, setDestination] = useState<Candidate | null>(null);
  const [source, setSource] = useState('');
  const [access, setAccess] = useState('');
  const [target, setTarget] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const [done, setDone] = useState(false);
  const [rows, setRows] = useState<Outcome[]>([]);
  const [matching, setMatching] = useState<Outcome | null>(null);
  const [previewOffset, setPreviewOffset] = useState(0);
  const [previewMore, setPreviewMore] = useState(false);
  useEffect(() => {
    setRows([]);
    setDone(false);
    setError(false);
    setPreviewOffset(0);
    setPreviewMore(false);
  }, [source, access, target, mode, country, destination?.id]);
  const stopped = useRef(false);
  useEffect(
    () => () => {
      stopped.current = true;
    },
    [],
  );
  useEffect(() => {
    onBusyChange(busy);
    return () => onBusyChange(false);
  }, [busy, onBusyChange]);
  const accounts = useQuery({
    queryKey: [...catalogAdminKey, 'direct-link-accounts'],
    queryFn: async () => {
      const [tg, hb] = await Promise.all(
        ['travelgate', 'hotelbeds'].map((connector) =>
          client.get<{ data: Account[] }>('hotel-catalog-admin/import-accounts', { params: { connector } }),
        ),
      );
      return {
        tg: tg.data.data.filter((a) => a.status === 'active'),
        hb: hb.data.data.filter((a) => a.status === 'active'),
      };
    },
    retry: false,
  });
  const selection =
    mode === 'all'
      ? { all: 'true' }
      : mode === 'country'
        ? { country_code: country }
        : { geo_place_id: destination?.id };
  const ready =
    !!source &&
    !!target &&
    !!access &&
    confirmed &&
    (mode === 'all' || (mode === 'country' ? /^[A-Z]{2}$/.test(country) : !!destination));
  async function preview(offset = 0) {
    if (busy) return;
    setBusy(true);
    setError(false);
    setDone(false);
    try {
      const page = (
        await client.get<{ data: { hotel_id: string; display_name: string }[]; meta: { has_more: boolean } }>(
          'hotel-catalog-admin/direct-linking/hotels',
          { params: { ...selection, offset } },
        )
      ).data;
      setRows(page.data.map((h) => ({ ...h, status: 'unprocessed' })));
      setPreviewOffset(offset);
      setPreviewMore(page.meta.has_more);
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  }
  async function run() {
    if (!ready || busy) return;
    stopped.current = false;
    setBusy(true);
    setError(false);
    setDone(false);
    setRows([]);
    setPreviewOffset(0);
    setPreviewMore(false);
    const seen = new Set<string>();
    try {
      for (let offset = 0; !stopped.current; offset += 25) {
        const page = (
          await client.get<{ data: { hotel_id: string; display_name: string }[]; meta: { has_more: boolean } }>(
            'hotel-catalog-admin/direct-linking/hotels',
            { params: { ...selection, offset } },
          )
        ).data;
        for (const hotel of page.data) {
          if (stopped.current) break;
          if (seen.has(hotel.hotel_id)) continue;
          seen.add(hotel.hotel_id);
          let result: { status: string; job_id?: string; code?: string };
          let job: string | undefined;
          do {
            if (stopped.current) break;
            result = (
              await client.post<{ data: typeof result }>('hotel-catalog-admin/direct-linking/advance', {
                hotel_id: hotel.hotel_id,
                source_account_id: source,
                source_access: access,
                target_account_id: target,
                confirm_source: true,
                ...(job ? { job_id: job } : {}),
              })
            ).data.data;
            job = result.job_id;
            if (!stopped.current)
              setRows((previous) => [
                ...previous.filter((row) => row.hotel_id !== hotel.hotel_id),
                { ...hotel, ...result },
              ]);
            if (result.status === 'pending') await new Promise((resolve) => setTimeout(resolve, 3000));
          } while (result!.status === 'pending');
        }
        if (!page.meta.has_more) {
          if (!stopped.current) setDone(true);
          break;
        }
      }
    } catch {
      if (!stopped.current) setError(true);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Stack spacing={2} sx={{ my: 2 }}>
      <Typography variant="h5">{t('hotelDirectLink.title')}</Typography>
      <Alert severity="info">{t('hotelDirectLink.help')}</Alert>
      {accounts.isError && <Alert severity="error">{t('hotelCatalogAdmin.loadError')}</Alert>}
      <TextField
        select
        label={t('hotelDirectLink.source')}
        value={source}
        disabled={busy}
        onChange={(e) => {
          setSource(e.target.value);
          setAccess('');
          setConfirmed(false);
        }}
      >
        {(accounts.data?.tg ?? []).map((a) => (
          <MenuItem key={a.connector_account_id} value={a.connector_account_id}>
            {a.display_name}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        label={t('hotelDirectLink.access')}
        value={access}
        disabled={busy}
        onChange={(e) => {
          setAccess(e.target.value);
          setConfirmed(false);
        }}
      >
        {(accounts.data?.tg.find((a) => a.connector_account_id === source)?.access_codes ?? []).map((code) => (
          <MenuItem key={code} value={code}>
            {code}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        label={t('hotelDirectLink.target')}
        value={target}
        disabled={busy}
        onChange={(e) => setTarget(e.target.value)}
      >
        {(accounts.data?.hb ?? []).map((a) => (
          <MenuItem key={a.connector_account_id} value={a.connector_account_id}>
            {a.display_name}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        label={t('hotelDirectLink.scope')}
        value={mode}
        disabled={busy}
        onChange={(e) => setMode(e.target.value)}
      >
        {['country', 'destination', 'all'].map((value) => (
          <MenuItem key={value} value={value}>
            {t(`hotelDirectLink.${value}`)}
          </MenuItem>
        ))}
      </TextField>
      {mode === 'country' && (
        <TextField
          label={t('hotelDirectLink.countryCode')}
          value={country}
          disabled={busy}
          inputProps={{ maxLength: 2 }}
          onChange={(e) => setCountry(e.target.value.toUpperCase())}
        />
      )}
      {mode === 'destination' && <DestinationPicker value={destination} disabled={busy} onChange={setDestination} />}
      <FormControlLabel
        control={<Checkbox checked={confirmed} disabled={busy} onChange={(e) => setConfirmed(e.target.checked)} />}
        label={t('hotelDirectLink.confirmSource')}
      />
      <Stack direction="row" spacing={1}>
        <Button
          disabled={
            busy || !target || !(mode === 'all' || (mode === 'country' ? /^[A-Z]{2}$/.test(country) : !!destination))
          }
          onClick={() => preview()}
        >
          {t('hotelDirectLink.preview')}
        </Button>
        <Button variant="contained" disabled={!ready || busy} onClick={run}>
          {t('hotelDirectLink.start')}
        </Button>
        {busy && (
          <Button
            onClick={() => {
              stopped.current = true;
            }}
          >
            {t('hotelDirectLink.pause')}
          </Button>
        )}
      </Stack>
      {error && <Alert severity="error">{t('hotelDirectLink.error')}</Alert>}
      {done && <Alert severity="success">{t('hotelDirectLink.finished')}</Alert>}
      <Typography>{t('hotelDirectLink.reviewed', { count: rows.length })}</Typography>
      {rows.map((row) => (
        <Stack key={row.hotel_id} direction="row" spacing={1} alignItems="center">
          <Typography>
            {row.display_name} ·{' '}
            {t(row.status === 'unprocessed' ? 'hotelDirectLink.unprocessed' : `hotelDirectLink.status.${row.status}`)}{' '}
            {row.code && `· ${row.code}`}
          </Typography>
          {['unprocessed', 'needs_review', 'missing_source', 'conflict', 'not_found'].includes(row.status) && (
            <Button disabled={busy} onClick={() => setMatching(row)}>
              {t('hotelDirectLink.review')}
            </Button>
          )}
        </Stack>
      ))}
      {(previewOffset > 0 || previewMore) && (
        <Stack direction="row">
          <Button disabled={busy || previewOffset === 0} onClick={() => preview(Math.max(0, previewOffset - 25))}>
            {t('hotelDestinationOrder.previousMatches')}
          </Button>
          <Button disabled={busy || !previewMore} onClick={() => preview(previewOffset + 25)}>
            {t('hotelDestinationOrder.nextMatches')}
          </Button>
        </Stack>
      )}
      {matching && (
        <DirectHotelMatch
          hotel={matching}
          account={target}
          onClose={() => setMatching(null)}
          onLinked={() => {
            setRows((previous) =>
              previous.map((row) => (row.hotel_id === matching.hotel_id ? { ...row, status: 'linked' } : row)),
            );
            setMatching(null);
          }}
        />
      )}
    </Stack>
  );
}
